import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

/* ─── CONFIRMATION MODAL ─── */
type ConfirmationType = 'creative' | 'church';

function ConfirmationModal({ type, onClose }: { type: ConfirmationType; onClose: () => void }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const isCreative = type === 'creative';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Card */}
      <div
        className="relative z-10 w-full max-w-lg bg-white rounded-3xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: 'confirmPop 0.45s cubic-bezier(0.22,1,0.36,1) both' }}
      >
        {/* Top accent bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-brand-yellow via-yellow-300 to-brand-yellow" />

        {/* Background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 bg-brand-yellow/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative px-8 pt-10 pb-10 text-center">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-base" />
          </button>

          {/* Icon */}
          <div
            className="w-20 h-20 flex items-center justify-center rounded-full bg-brand-yellow/15 border-4 border-brand-yellow/30 text-brand-yellow mx-auto mb-6"
            style={{ animation: 'confirmPop 0.5s cubic-bezier(0.22,1,0.36,1) 0.15s both' }}
          >
            <i className="ri-check-double-line text-4xl" />
          </div>

          {/* Headline */}
          <h2 className="font-heading text-3xl font-black text-brand-blue-dark mb-2">
            {isCreative ? 'Information Submitted!' : 'Request Submitted!'}
          </h2>
          <p className="font-body text-brand-yellow-dark font-semibold text-sm uppercase tracking-widest mb-6">
            {isCreative ? 'Creatives & Ministry Professionals' : 'Churches & Organizations'}
          </p>

          {/* Body */}
          <p className="font-body text-gray-500 text-base leading-relaxed mb-8">
            {isCreative
              ? 'Thank you for submitting your information. We will carefully review your profile, experience, and availability — and reach out when a matching opportunity becomes available.'
              : 'Thank you for reaching out. We will review your request and work diligently to connect you with the right individuals for your ministry.'}
          </p>

          {/* What happens next */}
          <div className="bg-[#F8F7FF] rounded-2xl p-5 mb-8 text-left">
            <p className="font-body text-xs font-bold text-brand-blue-dark uppercase tracking-widest mb-3">What Happens Next</p>
            <div className="space-y-3">
              {(isCreative ? [
                { icon: 'ri-search-eye-line', text: 'We review your profile and skill set' },
                { icon: 'ri-links-line', text: 'We match you with aligned opportunities' },
                { icon: 'ri-phone-line', text: 'We reach out to discuss next steps' },
              ] : [
                { icon: 'ri-file-search-line', text: 'We review your role and requirements' },
                { icon: 'ri-user-search-line', text: 'We identify qualified candidates for you' },
                { icon: 'ri-phone-line', text: 'We reach out to facilitate the connection' },
              ]).map((step) => (
                <div key={step.text} className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-brand-yellow/20 text-brand-yellow-dark flex-shrink-0">
                    <i className={`${step.icon} text-sm`} />
                  </div>
                  <span className="font-body text-sm text-gray-600">{step.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Free service note */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-heart-fill text-brand-yellow text-sm" />
            </div>
            <p className="font-body text-xs text-gray-400 italic">
              This is a free service — done for the glory of God and the advancement of ministry.
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={onClose}
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm hover:bg-brand-yellow-light transition-all cursor-pointer"
            >
              <i className="ri-check-line" /> Done
            </button>
            <Link
              to="/"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full border-2 border-gray-200 text-gray-500 font-body font-semibold text-sm hover:border-brand-purple hover:text-brand-purple transition-all cursor-pointer"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes confirmPop {
          0%   { opacity: 0; transform: scale(0.88) translateY(16px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}

type Tab = 'creatives' | 'organizations';

function HeroSection({ activeTab, setActiveTab }: { activeTab: Tab; setActiveTab: (t: Tab) => void }) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  return (
    <section className="relative min-h-[480px] flex flex-col items-center justify-center overflow-hidden pt-24 pb-0">
      {/* Background */}
      <img
        src="https://public.readdy.ai/ai/img_res/f26ab83bc8038aeae3f3b4cfb75fbf57.jpg"
        alt="Ministry & Creative Connections"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />

      <div ref={ref} className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        <span className={`scroll-hidden-pop ${isVisible ? 'scroll-visible' : ''} inline-block px-5 py-2 rounded-full bg-brand-yellow/20 border border-brand-yellow/40 text-brand-yellow font-body text-xs font-semibold uppercase tracking-widest mb-5`}>
          Free Service · For the Glory of God
        </span>
        <h1 className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} delay-100 font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-5 leading-tight`}>
          Ministry Staffing &<br />
          <span className="text-brand-yellow">Creative Connections</span>
        </h1>
        <p className={`scroll-hidden-blur ${isVisible ? 'scroll-visible' : ''} delay-200 font-body text-white/75 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed mb-8`}>
          We provide connections for ministries and creatives across music, media, production, and leadership.
          Whether you are looking to serve or looking for someone to strengthen your ministry, we are here to
          facilitate the connection with excellence and integrity.
        </p>
        <p className={`scroll-hidden-blur ${isVisible ? 'scroll-visible' : ''} delay-300 font-body text-white/50 text-sm mb-10`}>
          A free service offered by Creative Institute LLC — done for the glory of God and the advancement of ministry.
        </p>

        {/* Tab switcher */}
        <div className={`scroll-hidden-scale-blur ${isVisible ? 'scroll-visible' : ''} delay-400 inline-flex items-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-full p-1`}>
          <button
            onClick={() => setActiveTab('creatives')}
            className={`whitespace-nowrap px-6 py-2.5 rounded-full font-body font-semibold text-sm transition-all duration-300 cursor-pointer ${
              activeTab === 'creatives'
                ? 'bg-brand-yellow text-brand-blue-dark'
                : 'text-white/70 hover:text-white'
            }`}
          >
            Looking for Opportunities?
          </button>
          <button
            onClick={() => setActiveTab('organizations')}
            className={`whitespace-nowrap px-6 py-2.5 rounded-full font-body font-semibold text-sm transition-all duration-300 cursor-pointer ${
              activeTab === 'organizations'
                ? 'bg-brand-yellow text-brand-blue-dark'
                : 'text-white/70 hover:text-white'
            }`}
          >
            Looking to Fill a Role?
          </button>
        </div>
      </div>

      {/* Wave bottom */}
      <div className="relative z-10 w-full mt-12">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block">
          <path d="M0 60 C360 0 1080 0 1440 60 L1440 60 L0 60 Z" fill="#F8F7FF" />
        </svg>
      </div>
    </section>
  );
}

/* ─── CREATIVES TAB ─── */
function CreativesTab({ onScrollToForm }: { onScrollToForm: () => void }) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  return (
    <section className="py-20 bg-[#F8F7FF]">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-8">
        <div ref={ref} className="max-w-screen-md mx-auto">
          {/* Text */}
          <div>
            <span className={`scroll-hidden-pop ${isVisible ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-purple/10 text-brand-purple font-body text-xs font-semibold uppercase tracking-widest mb-5`}>
              Creatives &amp; Ministry Professionals
            </span>
            <h2 className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} delay-100 font-heading text-3xl sm:text-4xl font-black text-brand-blue-dark mb-5 leading-tight`}>
              Ready to Serve?<br />
              <span className="gradient-text">We'll Connect You.</span>
            </h2>
            <p className={`scroll-hidden-blur ${isVisible ? 'scroll-visible' : ''} delay-200 font-body text-gray-500 text-base leading-relaxed mb-4`}>
              If you are seeking opportunities in ministry or the creative space, we invite you to submit your information.
              We work with individuals across multiple areas including musicians, audio engineers, media teams, production staff,
              and ministry leadership roles.
            </p>
            <p className={`scroll-hidden-blur ${isVisible ? 'scroll-visible' : ''} delay-300 font-body text-gray-500 text-base leading-relaxed mb-4`}>
              Once submitted, we will review your experience, skill set, and availability and connect you with opportunities
              that align with your profile.
            </p>
            <p className={`scroll-hidden-blur ${isVisible ? 'scroll-visible' : ''} delay-400 font-body text-gray-500 text-base leading-relaxed mb-8`}>
              We also offer optional consultations to help you present yourself professionally, strengthen your positioning,
              and prepare for upcoming opportunities.
            </p>

            {/* Perks */}
            <div className={`scroll-hidden-blur ${isVisible ? 'scroll-visible' : ''} delay-500 grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8`}>
              {[
                { icon: 'ri-music-2-line', label: 'Musicians & Vocalists' },
                { icon: 'ri-mic-line', label: 'Audio Engineers' },
                { icon: 'ri-camera-line', label: 'Media & Camera Operators' },
                { icon: 'ri-live-line', label: 'Live Stream Technicians' },
                { icon: 'ri-user-star-line', label: 'Worship Leaders' },
                { icon: 'ri-team-line', label: 'Ministry Leadership' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-gray-100">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-purple/10 text-brand-purple flex-shrink-0">
                    <i className={`${item.icon} text-sm`} />
                  </div>
                  <span className="font-body text-sm text-brand-blue-dark font-medium">{item.label}</span>
                </div>
              ))}
            </div>

            <button
              onClick={onScrollToForm}
              className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm hover:bg-brand-yellow-light transition-all duration-300 cursor-pointer"
            >
              <i className="ri-send-plane-line" />
              Submit Your Information
            </button>
          </div>


        </div>
      </div>
    </section>
  );
}

/* ─── ORGANIZATIONS TAB ─── */
function OrganizationsTab({ onScrollToForm }: { onScrollToForm: () => void }) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  return (
    <section className="py-20 bg-[#F8F7FF]">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-8">
        <div ref={ref} className="max-w-screen-md mx-auto">
          {/* Text */}
          <div>
            <span className={`scroll-hidden-pop ${isVisible ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-yellow/20 text-brand-yellow-dark font-body text-xs font-semibold uppercase tracking-widest mb-5`}>
              Churches &amp; Organizations
            </span>
            <h2 className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} delay-100 font-heading text-3xl sm:text-4xl font-black text-brand-blue-dark mb-5 leading-tight`}>
              Need the Right Person?<br />
              <span className="gradient-text">We'll Find Them.</span>
            </h2>
            <p className={`scroll-hidden-blur ${isVisible ? 'scroll-visible' : ''} delay-200 font-body text-gray-500 text-base leading-relaxed mb-4`}>
              If your ministry or organization is in need of support, we are here to help you find the right individuals.
              We assist with connecting churches and organizations to qualified individuals in areas such as music, audio,
              media, production, and ministry leadership.
            </p>
            <p className={`scroll-hidden-blur ${isVisible ? 'scroll-visible' : ''} delay-300 font-body text-gray-500 text-base leading-relaxed mb-4`}>
              Please submit details including the role, expectations, schedule, and budget, and we will work to connect
              you with individuals that align with your needs.
            </p>
            <p className={`scroll-hidden-blur ${isVisible ? 'scroll-visible' : ''} delay-400 font-body text-gray-500 text-base leading-relaxed mb-8`}>
              Our goal is to bring structure, excellence, and the right fit to every opportunity.
            </p>

            {/* Perks */}
            <div className={`scroll-hidden-blur ${isVisible ? 'scroll-visible' : ''} delay-500 grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8`}>
              {[
                { icon: 'ri-music-2-line', label: 'Musicians' },
                { icon: 'ri-equalizer-line', label: 'Audio Engineers' },
                { icon: 'ri-camera-line', label: 'Media Teams' },
                { icon: 'ri-live-line', label: 'Live Stream Support' },
                { icon: 'ri-user-star-line', label: 'Minister of Music' },
                { icon: 'ri-team-line', label: 'Creative Directors' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-gray-100">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-yellow/15 text-brand-yellow-dark flex-shrink-0">
                    <i className={`${item.icon} text-sm`} />
                  </div>
                  <span className="font-body text-sm text-brand-blue-dark font-medium">{item.label}</span>
                </div>
              ))}
            </div>

            <button
              onClick={onScrollToForm}
              className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm hover:bg-brand-yellow-light transition-all duration-300 cursor-pointer"
            >
              <i className="ri-send-plane-line" />
              Submit Your Request
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── HOW IT WORKS ─── */
const CREATIVE_STEPS = [
  {
    num: '01',
    icon: 'ri-file-user-line',
    title: 'Submit Your Profile',
    body: 'Fill out the intake form with your skills, experience, availability, and what you are looking for in a ministry role.',
  },
  {
    num: '02',
    icon: 'ri-search-eye-line',
    title: 'We Review Your Info',
    body: 'Our team carefully reviews your profile, skill set, and heart for ministry to understand who you are and what you bring.',
  },
  {
    num: '03',
    icon: 'ri-links-line',
    title: 'We Make the Match',
    body: 'When a church or organization aligns with your profile, we facilitate the introduction and help move things forward.',
  },
  {
    num: '04',
    icon: 'ri-phone-line',
    title: 'We Reach Out to You',
    body: 'We contact you directly to discuss the opportunity, answer questions, and support you through the next steps.',
  },
];

const CHURCH_STEPS = [
  {
    num: '01',
    icon: 'ri-building-4-line',
    title: 'Submit Your Request',
    body: 'Tell us about your ministry, the role you need filled, your schedule, budget, and the type of person you are looking for.',
  },
  {
    num: '02',
    icon: 'ri-file-search-line',
    title: 'We Review Your Needs',
    body: 'Our team reviews your request and begins identifying individuals in our network who align with your requirements.',
  },
  {
    num: '03',
    icon: 'ri-user-search-line',
    title: 'We Find the Right Fit',
    body: 'We match your role with qualified creatives and ministry professionals who meet your experience and culture expectations.',
  },
  {
    num: '04',
    icon: 'ri-shake-hands-line',
    title: 'We Facilitate the Connection',
    body: 'We introduce both parties and support the connection process — all decisions and agreements are made directly between you.',
  },
];

function HowItWorksSection({ activeTab }: { activeTab: Tab }) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const isCreative = activeTab === 'creatives';
  const steps = isCreative ? CREATIVE_STEPS : CHURCH_STEPS;
  const accent = isCreative ? 'bg-brand-purple/10 text-brand-purple border-brand-purple/20' : 'bg-brand-yellow/15 text-brand-yellow-dark border-brand-yellow/30';
  const numColor = isCreative ? 'text-brand-purple' : 'text-brand-yellow-dark';
  const iconBg = isCreative ? 'bg-brand-purple/10 text-brand-purple' : 'bg-brand-yellow/15 text-brand-yellow-dark';
  const connectorColor = isCreative ? 'bg-brand-purple/20' : 'bg-brand-yellow/30';

  return (
    <section className="py-20 bg-white">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-8">
        <div ref={ref} className="text-center mb-14">
          <span className={`scroll-hidden-pop ${isVisible ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full border font-body text-xs font-semibold uppercase tracking-widest mb-5 ${accent}`}>
            How It Works
          </span>
          <h2 className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} delay-100 font-heading text-4xl sm:text-5xl font-black text-brand-blue-dark mb-4 leading-tight`}>
            Simple. Transparent. <span className="gradient-text">Free.</span>
          </h2>
          <p className={`scroll-hidden-blur ${isVisible ? 'scroll-visible' : ''} delay-200 font-body text-gray-500 text-base max-w-xl mx-auto leading-relaxed`}>
            {isCreative
              ? 'Here is exactly what happens from the moment you submit your information to the moment you get connected.'
              : 'Here is exactly what happens from the moment you submit your request to the moment we find your person.'}
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {/* Connector line — desktop only */}
          <div className={`hidden lg:block absolute top-10 left-[12.5%] right-[12.5%] h-px ${connectorColor} z-0`} />

          {steps.map((step, i) => (
            <div
              key={step.num}
              className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} relative z-10 flex flex-col items-center text-center`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              {/* Number + icon stack */}
              <div className="relative mb-5">
                <div className={`w-20 h-20 flex items-center justify-center rounded-2xl bg-white border-2 ${isCreative ? 'border-brand-purple/20' : 'border-brand-yellow/30'}`}>
                  <i className={`${step.icon} text-3xl ${isCreative ? 'text-brand-purple' : 'text-brand-yellow-dark'}`} />
                </div>
                <span className={`absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full bg-white border-2 ${isCreative ? 'border-brand-purple/30' : 'border-brand-yellow/40'} font-heading font-black text-[10px] ${numColor}`}>
                  {i + 1}
                </span>
              </div>

              <h3 className="font-heading text-brand-blue-dark font-black text-base mb-2 leading-snug">{step.title}</h3>
              <p className="font-body text-gray-500 text-sm leading-relaxed">{step.body}</p>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <div className={`scroll-hidden-blur ${isVisible ? 'scroll-visible' : ''} mt-14 flex flex-col sm:flex-row items-center justify-center gap-3 text-center`}>
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-yellow/15 text-brand-yellow-dark flex-shrink-0">
            <i className="ri-time-line text-sm" />
          </div>
          <p className="font-body text-gray-400 text-sm">
            Typical response time is <strong className="text-brand-blue-dark">1–2 business days</strong> after submission. This is a free service — no fees, no contracts, no pressure.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─── CREATIVES FORM ─── */
function CreativesForm({ onSubmitted }: { onSubmitted: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="fit_statement"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) return;
    setSubmitting(true);
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((val, key) => body.append(key, val.toString()));
    try {
      await fetch('https://readdy.ai/api/form/d7elamrfimgqccpmacj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch {
      // still show success
    } finally {
      setSubmitting(false);
      onSubmitted();
    }
  };

  return (
    <form
      id="creatives-intake-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="space-y-8"
    >
      {/* Basic Information */}
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-blue-dark mb-4 pb-2 border-b border-gray-100">Basic Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Full Name <span className="text-red-400">*</span></label>
            <input name="full_name" type="text" required placeholder="Your full name" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-purple bg-white" />
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Phone Number <span className="text-red-400">*</span></label>
            <input name="phone" type="tel" required placeholder="(910) 000-0000" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-purple bg-white" />
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Email Address <span className="text-red-400">*</span></label>
            <input name="email" type="email" required placeholder="you@example.com" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-purple bg-white" />
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">City &amp; State <span className="text-red-400">*</span></label>
            <input name="city_state" type="text" required placeholder="Fayetteville, NC" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-purple bg-white" />
          </div>
        </div>
      </div>

      {/* Area of Service */}
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-blue-dark mb-4 pb-2 border-b border-gray-100">Area of Service <span className="font-body text-xs text-gray-400 font-normal">(Select All That Apply)</span></h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            'Musician (Keyboard, Organ, Drums, Bass, Guitar, etc.)',
            'Vocalist',
            'Audio Engineer',
            'Media / Camera Operator',
            'Video Editor',
            'Live Stream Technician',
            'Minister of Music',
            'Worship Leader',
            'Creative Director',
          ].map((area) => (
            <label key={area} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-100 bg-white hover:border-brand-purple/30 cursor-pointer transition-colors">
              <input type="checkbox" name="area_of_service" value={area} className="w-4 h-4 accent-brand-purple" />
              <span className="font-body text-sm text-brand-blue-dark">{area}</span>
            </label>
          ))}
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-100 bg-white">
            <input type="checkbox" name="area_of_service" value="Other" className="w-4 h-4 accent-brand-purple" />
            <span className="font-body text-sm text-brand-blue-dark">Other:</span>
            <input name="area_other" type="text" placeholder="Please specify" className="flex-1 border-b border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-purple bg-transparent" />
          </div>
        </div>
      </div>

      {/* Experience */}
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-blue-dark mb-4 pb-2 border-b border-gray-100">Experience</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Years of Experience</label>
            <input name="years_experience" type="text" placeholder="e.g. 5 years" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-purple bg-white" />
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Church Experience</label>
            <select name="church_experience" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-purple bg-white cursor-pointer">
              <option value="">Select...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Previous Organizations or Churches Served</label>
            <input name="previous_orgs" type="text" placeholder="List any previous churches or organizations" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-purple bg-white" />
          </div>
        </div>
      </div>

      {/* Skill Level */}
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-blue-dark mb-4 pb-2 border-b border-gray-100">Skill Level</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {['Beginner', 'Intermediate', 'Advanced', 'Professional'].map((level) => (
            <label key={level} className="flex items-center gap-2 px-4 py-3 rounded-xl border border-gray-100 bg-white hover:border-brand-purple/30 cursor-pointer transition-colors">
              <input type="radio" name="skill_level" value={level} className="w-4 h-4 accent-brand-purple" />
              <span className="font-body text-sm text-brand-blue-dark">{level}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Availability */}
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-blue-dark mb-4 pb-2 border-b border-gray-100">Availability</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {['Sundays Only', 'Weekly Services', 'Rehearsals + Services', 'Flexible'].map((avail) => (
            <label key={avail} className="flex items-center gap-2 px-4 py-3 rounded-xl border border-gray-100 bg-white hover:border-brand-purple/30 cursor-pointer transition-colors">
              <input type="radio" name="availability" value={avail} className="w-4 h-4 accent-brand-purple" />
              <span className="font-body text-sm text-brand-blue-dark">{avail}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Compensation */}
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-blue-dark mb-4 pb-2 border-b border-gray-100">Preferred Compensation</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          {['Volunteer', 'Paid Only', 'Open to Both'].map((comp) => (
            <label key={comp} className="flex items-center gap-2 px-4 py-3 rounded-xl border border-gray-100 bg-white hover:border-brand-purple/30 cursor-pointer transition-colors">
              <input type="radio" name="compensation" value={comp} className="w-4 h-4 accent-brand-purple" />
              <span className="font-body text-sm text-brand-blue-dark">{comp}</span>
            </label>
          ))}
        </div>
        <div>
          <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Desired Weekly Rate <span className="font-normal text-gray-400">(Optional)</span></label>
          <input name="desired_rate" type="text" placeholder="e.g. $150/week" className="w-full sm:w-64 px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-purple bg-white" />
        </div>
      </div>

      {/* Travel */}
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-blue-dark mb-4 pb-2 border-b border-gray-100">Willing to Travel?</h3>
        <div className="flex items-center gap-4 mb-3">
          {['Yes', 'No'].map((opt) => (
            <label key={opt} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-100 bg-white hover:border-brand-purple/30 cursor-pointer transition-colors">
              <input type="radio" name="willing_to_travel" value={opt} className="w-4 h-4 accent-brand-purple" />
              <span className="font-body text-sm text-brand-blue-dark">{opt}</span>
            </label>
          ))}
        </div>
        <div>
          <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">If yes, how far?</label>
          <input name="travel_distance" type="text" placeholder="e.g. Within 30 miles" className="w-full sm:w-64 px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-purple bg-white" />
        </div>
      </div>

      {/* Relocation */}
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-blue-dark mb-1 pb-2 border-b border-gray-100">Willing to Relocate?</h3>
        <p className="font-body text-gray-400 text-xs mb-4">Would you consider relocating for the right ministry opportunity?</p>
        <div className="flex flex-wrap gap-3 mb-4">
          {['Yes, open to relocation', 'Possibly, depends on the opportunity', 'No, local only'].map((opt) => (
            <label key={opt} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-100 bg-white hover:border-brand-purple/30 cursor-pointer transition-colors">
              <input type="radio" name="willing_to_relocate" value={opt} className="w-4 h-4 accent-brand-purple" />
              <span className="font-body text-sm text-brand-blue-dark">{opt}</span>
            </label>
          ))}
        </div>
        <div>
          <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">
            Preferred Region / State <span className="font-normal text-gray-400">(Optional)</span>
          </label>
          <input
            name="relocation_preference"
            type="text"
            placeholder="e.g. Southeast US, anywhere in NC"
            className="w-full sm:w-72 px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-purple bg-white"
          />
        </div>
      </div>

      {/* Upload / Links */}
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-blue-dark mb-4 pb-2 border-b border-gray-100">Portfolio &amp; Links <span className="font-body text-xs text-gray-400 font-normal">(Optional)</span></h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">YouTube / Instagram Link</label>
            <input name="video_link" type="url" placeholder="https://youtube.com/..." className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-purple bg-white" />
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Portfolio Link</label>
            <input name="portfolio_link" type="url" placeholder="https://..." className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-purple bg-white" />
          </div>
          <div className="sm:col-span-2">
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Resume</label>
            <div className="w-full px-4 py-3 rounded-lg border border-dashed border-gray-300 bg-white text-center">
              <p className="font-body text-sm text-gray-400">File uploads are not collected — please share a link or describe your experience below.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Final Question */}
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-blue-dark mb-4 pb-2 border-b border-gray-100">Final Question</h3>
        <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">
          What makes you a great fit for ministry and team environments? <span className="text-red-400">*</span>
        </label>
        <textarea
          name="fit_statement"
          required
          maxLength={500}
          rows={5}
          placeholder="Share your heart, your experience, and what drives you to serve..."
          onChange={(e) => setCharCount(e.target.value.length)}
          className="w-full px-4 py-3 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-purple bg-white resize-none"
        />
        <p className={`font-body text-xs mt-1 text-right ${charCount > 480 ? 'text-red-400' : 'text-gray-400'}`}>{charCount}/500</p>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="whitespace-nowrap w-full sm:w-auto inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm hover:bg-brand-yellow-light transition-all duration-300 cursor-pointer disabled:opacity-60"
      >
        {submitting ? <><i className="ri-loader-4-line animate-spin" /> Submitting...</> : <><i className="ri-send-plane-fill" /> Submit Your Information</>}
      </button>
    </form>
  );
}

/* ─── ORGANIZATIONS FORM ─── */
function OrganizationsForm({ onSubmitted }: { onSubmitted: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="additional_notes"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) return;
    setSubmitting(true);
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((val, key) => body.append(key, val.toString()));
    try {
      await fetch('https://readdy.ai/api/form/d7elanjfimgqccpmacjg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch {
      // still show success
    } finally {
      setSubmitting(false);
      onSubmitted();
    }
  };

  return (
    <form
      id="organizations-request-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="space-y-8"
    >
      {/* Organization Info */}
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-blue-dark mb-4 pb-2 border-b border-gray-100">Organization Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Church / Organization Name <span className="text-red-400">*</span></label>
            <input name="org_name" type="text" required placeholder="Your church or organization" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-yellow bg-white" />
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Contact Person Name <span className="text-red-400">*</span></label>
            <input name="contact_name" type="text" required placeholder="Your name" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-yellow bg-white" />
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Phone Number <span className="text-red-400">*</span></label>
            <input name="phone" type="tel" required placeholder="(910) 000-0000" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-yellow bg-white" />
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Email Address <span className="text-red-400">*</span></label>
            <input name="email" type="email" required placeholder="you@church.org" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-yellow bg-white" />
          </div>
          <div className="sm:col-span-2">
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">City &amp; State <span className="text-red-400">*</span></label>
            <input name="city_state" type="text" required placeholder="Fayetteville, NC" className="w-full sm:w-64 px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-yellow bg-white" />
          </div>
        </div>
      </div>

      {/* What Are You Looking For */}
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-blue-dark mb-4 pb-2 border-b border-gray-100">What Are You Looking For? <span className="font-body text-xs text-gray-400 font-normal">(Select All That Apply)</span></h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            'Musician',
            'Audio Engineer',
            'Media Team',
            'Live Stream Support',
            'Minister of Music',
            'Worship Leader',
            'Creative Director',
          ].map((role) => (
            <label key={role} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-100 bg-white hover:border-brand-yellow/40 cursor-pointer transition-colors">
              <input type="checkbox" name="looking_for" value={role} className="w-4 h-4 accent-brand-yellow" />
              <span className="font-body text-sm text-brand-blue-dark">{role}</span>
            </label>
          ))}
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-100 bg-white">
            <input type="checkbox" name="looking_for" value="Other" className="w-4 h-4 accent-brand-yellow" />
            <span className="font-body text-sm text-brand-blue-dark">Other:</span>
            <input name="looking_for_other" type="text" placeholder="Please specify" className="flex-1 border-b border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-yellow bg-transparent" />
          </div>
        </div>
      </div>

      {/* Position Details */}
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-blue-dark mb-4 pb-2 border-b border-gray-100">Position Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Role Title <span className="text-red-400">*</span></label>
            <input name="role_title" type="text" required placeholder="e.g. Worship Leader" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-yellow bg-white" />
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Description of Responsibilities</label>
            <input name="responsibilities" type="text" placeholder="Brief description of the role" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-yellow bg-white" />
          </div>
        </div>
      </div>

      {/* Schedule */}
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-blue-dark mb-4 pb-2 border-b border-gray-100">Schedule</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Rehearsal Day(s)</label>
            <input name="rehearsal_days" type="text" placeholder="e.g. Thursdays" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-yellow bg-white" />
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Service Day(s)</label>
            <input name="service_days" type="text" placeholder="e.g. Sundays" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-yellow bg-white" />
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Frequency</label>
            <select name="frequency" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-yellow bg-white cursor-pointer">
              <option value="">Select...</option>
              <option value="Weekly">Weekly</option>
              <option value="Bi-Weekly">Bi-Weekly</option>
              <option value="Monthly">Monthly</option>
            </select>
          </div>
        </div>
      </div>

      {/* Budget */}
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-blue-dark mb-4 pb-2 border-b border-gray-100">Budget</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Weekly Budget Range</label>
            <input name="weekly_budget" type="text" placeholder="e.g. $100 – $250/week" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-yellow bg-white" />
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">Open to Negotiation?</label>
            <select name="open_to_negotiation" className="w-full px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-yellow bg-white cursor-pointer">
              <option value="">Select...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
        </div>
      </div>

      {/* Experience Level Desired */}
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-blue-dark mb-4 pb-2 border-b border-gray-100">Experience Level Desired</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {['Beginner', 'Intermediate', 'Advanced', 'Professional'].map((level) => (
            <label key={level} className="flex items-center gap-2 px-4 py-3 rounded-xl border border-gray-100 bg-white hover:border-brand-yellow/40 cursor-pointer transition-colors">
              <input type="radio" name="experience_level" value={level} className="w-4 h-4 accent-brand-yellow" />
              <span className="font-body text-sm text-brand-blue-dark">{level}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Additional Notes */}
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-blue-dark mb-4 pb-2 border-b border-gray-100">Additional Notes</h3>
        <textarea
          name="additional_notes"
          maxLength={500}
          rows={5}
          placeholder="Share your ministry culture, expectations, specific requirements, or anything else that would help us find the right fit..."
          onChange={(e) => setCharCount(e.target.value.length)}
          className="w-full px-4 py-3 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-yellow bg-white resize-none"
        />
        <p className={`font-body text-xs mt-1 text-right ${charCount > 480 ? 'text-red-400' : 'text-gray-400'}`}>{charCount}/500</p>
      </div>

      {/* Relocation */}
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-blue-dark mb-1 pb-2 border-b border-gray-100">Relocation</h3>
        <p className="font-body text-gray-400 text-xs mb-4">Is the candidate required or expected to relocate for this role?</p>
        <div className="flex flex-wrap gap-3 mb-4">
          {['Yes, relocation required', 'Preferred but not required', 'No relocation needed', 'Open to discussion'].map((opt) => (
            <label key={opt} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-100 bg-white hover:border-brand-yellow/40 cursor-pointer transition-colors">
              <input type="radio" name="relocation" value={opt} className="w-4 h-4 accent-brand-yellow" />
              <span className="font-body text-sm text-brand-blue-dark">{opt}</span>
            </label>
          ))}
        </div>
        <div>
          <label className="block font-body text-sm font-semibold text-brand-blue-dark mb-1.5">
            Location / City &amp; State <span className="font-normal text-gray-400">(if relocation applies)</span>
          </label>
          <input
            name="relocation_location"
            type="text"
            placeholder="e.g. Atlanta, GA"
            className="w-full sm:w-72 px-4 py-2.5 rounded-lg border border-gray-200 font-body text-sm text-brand-blue-dark focus:outline-none focus:border-brand-yellow bg-white"
          />
        </div>
      </div>

      {/* Urgency */}
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-blue-dark mb-4 pb-2 border-b border-gray-100">Urgency</h3>
        <div className="flex flex-wrap gap-3">
          {['ASAP', 'Within 30 Days', 'Flexible'].map((urgency) => (
            <label key={urgency} className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-100 bg-white hover:border-brand-yellow/40 cursor-pointer transition-colors">
              <input type="radio" name="urgency" value={urgency} className="w-4 h-4 accent-brand-yellow" />
              <span className="font-body text-sm text-brand-blue-dark">{urgency}</span>
            </label>
          ))}
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="whitespace-nowrap w-full sm:w-auto inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm hover:bg-brand-yellow-light transition-all duration-300 cursor-pointer disabled:opacity-60"
      >
        {submitting ? <><i className="ri-loader-4-line animate-spin" /> Submitting...</> : <><i className="ri-send-plane-fill" /> Submit Your Request</>}
      </button>
    </form>
  );
}

/* ─── FORM SECTION ─── */
function FormSection({
  activeTab,
  formRef,
  onCreativeSubmitted,
  onChurchSubmitted,
}: {
  activeTab: Tab;
  formRef: React.RefObject<HTMLDivElement>;
  onCreativeSubmitted: () => void;
  onChurchSubmitted: () => void;
}) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.05 });
  return (
    <section className="py-20 bg-white" ref={formRef}>
      <div className="max-w-screen-lg mx-auto px-4 sm:px-8">
        <div ref={ref} className={`scroll-hidden-blur ${isVisible ? 'scroll-visible' : ''}`}>
          {/* Header */}
          <div className="text-center mb-12">
            <span className="inline-block px-5 py-2 rounded-full bg-brand-yellow/15 text-brand-yellow-dark font-body text-xs font-semibold uppercase tracking-widest mb-4">
              {activeTab === 'creatives' ? 'Creatives & Ministry Professionals' : 'Churches & Organizations'}
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-black text-brand-blue-dark mb-3">
              {activeTab === 'creatives' ? 'Submit Your Information' : 'Submit Your Request'}
            </h2>
            <p className="font-body text-gray-500 text-base max-w-xl mx-auto">
              {activeTab === 'creatives'
                ? 'Fill out the form below and we will review your profile and connect you with opportunities that align with your gifts.'
                : 'Fill out the form below and we will work to connect you with the right individuals for your ministry.'}
            </p>
          </div>

          {/* Form card */}
          <div className="bg-[#F8F7FF] rounded-3xl p-6 sm:p-10 border border-gray-100">
            {activeTab === 'creatives'
              ? <CreativesForm onSubmitted={onCreativeSubmitted} />
              : <OrganizationsForm onSubmitted={onChurchSubmitted} />}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── DISCLAIMER ─── */
function DisclaimerSection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  return (
    <section className="py-16 bg-[#F8F7FF]">
      <div className="max-w-screen-lg mx-auto px-4 sm:px-8">
        <div ref={ref} className={`scroll-hidden-blur ${isVisible ? 'scroll-visible' : ''} bg-white rounded-3xl border border-amber-100 p-8 sm:p-12`}>
          <div className="flex items-start gap-5 mb-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-amber-50 text-amber-500 flex-shrink-0">
              <i className="ri-information-line text-xl" />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-black text-brand-blue-dark mb-1">Service Disclaimer</h2>
              <p className="font-body text-gray-400 text-sm">Please read before submitting</p>
            </div>
          </div>
          <div className="space-y-4 font-body text-gray-500 text-sm leading-relaxed">
            <p>
              Creative Institute LLC serves as a <strong className="text-brand-blue-dark">connection platform</strong> between creatives, ministry professionals, and organizations.
            </p>
            <p>
              While we strive to make strong and effective connections, we <strong className="text-brand-blue-dark">do not guarantee placement, employment, or long-term agreements.</strong>
            </p>
            <p>
              All final decisions, negotiations, and agreements are made <strong className="text-brand-blue-dark">directly between the parties involved.</strong>
            </p>
            <p>
              Creative Institute LLC is not responsible for any agreements, disputes, or outcomes following a connection.
            </p>
            <p>
              This service is provided <strong className="text-brand-blue-dark">free of charge</strong> to serve the community and support the growth of ministry and creative excellence.
            </p>
          </div>
          <div className="mt-8 pt-6 border-t border-gray-100 flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-yellow/15 text-brand-yellow-dark flex-shrink-0">
              <i className="ri-heart-fill text-sm" />
            </div>
            <p className="font-body text-xs text-gray-400 italic">
              This is a free service offered by Creative Institute LLC — done for the glory of God and the advancement of ministry.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── PAGE ─── */
export default function MinistryConnectionsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('creatives');
  const [confirmation, setConfirmation] = useState<ConfirmationType | null>(null);
  const formRef = { current: null as HTMLDivElement | null };

  const scrollToForm = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <main>
      {confirmation && (
        <ConfirmationModal type={confirmation} onClose={() => setConfirmation(null)} />
      )}
      <HeroSection activeTab={activeTab} setActiveTab={setActiveTab} />
      {activeTab === 'creatives' ? (
        <CreativesTab onScrollToForm={scrollToForm} />
      ) : (
        <OrganizationsTab onScrollToForm={scrollToForm} />
      )}
      <HowItWorksSection activeTab={activeTab} />
      <FormSection
        activeTab={activeTab}
        formRef={formRef as React.RefObject<HTMLDivElement>}
        onCreativeSubmitted={() => setConfirmation('creative')}
        onChurchSubmitted={() => setConfirmation('church')}
      />
      <DisclaimerSection />
    </main>
  );
}
